import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  IconButton,
  Snackbar,
  CardMedia,
} from '@mui/material';
import {
  ArrowBack,
  Favorite,
  FavoriteBorder,
  Share,
  Store,
  LocationOn,
  AccessTime,
  Navigation,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import { url } from '../constants';

// ✅ Helper to fix the missing image path and link it to the backend server
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

// ✅ Helper to check if a date string is actually valid
const isValidDate = (dateString) => {
  if (!dateString || dateString.trim() === '') return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.valueOf());
};

const DealDetails = () => {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [similarDeals, setSimilarDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userLocation } = useLocationContext();

  // Load categories from localStorage
  useEffect(() => {
    const savedCategories = localStorage.getItem('categories');
    if (savedCategories) {
      try {
        const parsedCategories = JSON.parse(savedCategories);
        if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
          setCategories(parsedCategories);
        }
      } catch (error) {
        console.error('❌ Error parsing categories:', error);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsLoggedIn(!!token);
    fetchDealDetails();
  }, [id]);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('userFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  useEffect(() => {
    if (deal) {
      checkIfFavorite();
      setPage(0);
      setSimilarDeals([]);
      setHasMore(true);
      fetchSimilarDeals(0, true);
    }
  }, [deal?._id]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!loadMoreRef.current || loadingMore || !hasMore || similarDeals.length === 0) {
      return;
    }

    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMore && !loadingMore) {
          loadMoreSimilarDeals();
        }
      });
    };

    observerRef.current = new IntersectionObserver(callback, options);
    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, similarDeals.length, page]);

  // Open in Google Maps function for the deal's location
  const openInGoogleMaps = () => {
    if (!deal?.location?.coordinates || deal.location.coordinates.length !== 2) {
      return;
    }
    const [lng, lat] = deal.location.coordinates;
    const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(mapsUrl, '_blank');
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Get category color from localStorage
  const getCategoryColor = (categoryName) => {
    const savedCategory = categories.find(cat => 
      cat.cate_name === categoryName || 
      categoryName.includes(cat.cate_name) ||
      cat.cate_name.includes(categoryName)
    );
    
    if (savedCategory && savedCategory.cate_color) {
      return savedCategory.cate_color;
    }

    const colorMap = {
      'Electronics': '#FF6B6B',
      'Fashion': '#4ECDC4',
      'Food': '#45B7D1',
      'Grocery': '#96CEB4',
      'Travel': '#FECA57',
      'Home': '#FF9FF3',
      'Beauty': '#54A0FF',
      'Books': '#5F27CD',
      'Sports': '#00D2D3',
      'Groceries': '#1a7f3a',
      'Food & Dining': '#FF6B6B',
      'Shopping & Retail': '#4ECDC4',
    };
    
    return colorMap[categoryName] || '#1a7f3a';
  };

  // Much darker shade for category chip
  const getDarkCategoryChipColor = (categoryName) => {
    const color = getCategoryColor(categoryName);
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkerR = Math.max(0, Math.floor(r * 0.6));
    const darkerG = Math.max(0, Math.floor(g * 0.6));
    const darkerB = Math.max(0, Math.floor(b * 0.6));
    
    return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
  };

  const fetchDealDetails = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${url}/deals/${id}`);
      
      if (!response.ok) {
        throw new Error('Deal not found');
      }

      const data = await response.json();
      
      if (userLocation && userLocation.lat && userLocation.lng && data.location?.coordinates) {
        const [dealLng, dealLat] = data.location.coordinates;
        const distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          dealLat,
          dealLng
        );
        data.distance = parseFloat(distance.toFixed(1));
      }

      setDeal(data);
    } catch (error) {
      console.error('❌ Error fetching deal:', error);
      setError(error.message || 'Failed to load deal details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSimilarDeals = async (pageNum, reset = false) => {
    if (!deal || !deal.cate_id) {
      return;
    }
  
    if (reset) {
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }
  
    try {
      const skip = pageNum * 20;
      let endpoint = `${url}/deals/all?limit=20&skip=${skip}`;
      
      endpoint += `&category_ids=${deal.cate_id}`;
      endpoint += `&exclude_deal_id=${deal._id}`;
      
      if (userLocation && userLocation.lat && userLocation.lng) {
        endpoint += `&lat=${userLocation.lat}&lng=${userLocation.lng}&sort_by=distance&sort_order=asc`;
      }
  
      const response = await fetch(endpoint);
      if (!response.ok) {
        return;
      }

      let data = await response.json();

      // Calculate distance for each deal
      if (userLocation && userLocation.lat && userLocation.lng) {
        data = data.map(deal => {
          if (deal.location?.coordinates) {
            const [dealLng, dealLat] = deal.location.coordinates;
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              dealLat,
              dealLng
            );
            return {
              ...deal,
              distance: parseFloat(distance.toFixed(1))
            };
          }
          return deal;
        });
      }
  
      if (data.length < 20) {
        setHasMore(false);
      }
  
      if (reset) {
        setSimilarDeals(data);
      } else {
        setSimilarDeals(prev => [...prev, ...data]);
      }
    } catch (error) {
      console.error('❌ Error fetching similar deals:', error);
    } finally {
      setLoadingMore(false);
    }
  };
  
  const loadMoreSimilarDeals = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    fetchSimilarDeals(nextPage, false);
  }, [page, loadingMore, hasMore, deal?.cate_id]);

  const checkIfFavorite = () => {
    const savedFavorites = localStorage.getItem('userFavorites');
    if (savedFavorites) {
      const favorites = JSON.parse(savedFavorites);
      setIsFavorite(favorites.includes(deal._id));
    }
  };

  const handleToggleFavorite = async () => {
    if (!isLoggedIn) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);

    const savedFavorites = localStorage.getItem('userFavorites');
    let favs = savedFavorites ? JSON.parse(savedFavorites) : [];
    
    if (newFavoriteState) {
      favs.push(deal._id);
    } else {
      favs = favs.filter(fav => fav !== deal._id);
    }
    
    setFavorites(favs);
    localStorage.setItem('userFavorites', JSON.stringify(favs));

    try {
      const token = localStorage.getItem('access_token');
      const endpoint = newFavoriteState 
        ? `${url}/deals/add-favorite` 
        : `${url}/deals/remove-favorite`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ deal_id: deal._id })
      });

      if (!response.ok) {
        throw new Error('Failed to update favorites');
      }

      setSnackbar({
        open: true,
        message: newFavoriteState ? 'Added to favorites' : 'Removed from favorites',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating favorites:', error);
      setIsFavorite(!newFavoriteState);
      setSnackbar({
        open: true,
        message: 'Failed to update favorites',
        severity: 'error'
      });
    }
  };

  const handleToggleFavoriteForSimilar = async (dealId) => {
    if (!isLoggedIn) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    const isFav = favorites.includes(dealId);
    
    let updatedFavorites;
    if (isFav) {
      updatedFavorites = favorites.filter(fav => fav !== dealId);
    } else {
      updatedFavorites = [...favorites, dealId];
    }
    
    setFavorites(updatedFavorites);
    localStorage.setItem('userFavorites', JSON.stringify(updatedFavorites));

    try {
      const token = localStorage.getItem('access_token');
      const endpoint = isFav 
        ? `${url}/deals/remove-favorite` 
        : `${url}/deals/add-favorite`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ deal_id: dealId })
      });

      if (!response.ok) {
        throw new Error('Failed to update favorites');
      }

      setSnackbar({
        open: true,
        message: isFav ? 'Removed from favorites' : 'Added to favorites',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating favorites:', error);
      setFavorites(favorites);
      localStorage.setItem('userFavorites', JSON.stringify(favorites));
      
      setSnackbar({
        open: true,
        message: 'Failed to update favorites',
        severity: 'error'
      });
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share && navigator.canShare && navigator.canShare({ url: window.location.href })) {
        await navigator.share({
          title: deal.deal_name,
          text: `Check out this deal: ${deal.percentage_discount}% off!`,
          url: window.location.href,
        });
        setSnackbar({
          open: true,
          message: 'Shared successfully!',
          severity: 'success'
        });
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('User cancelled share');
        return;
      }
    }
  
    try {
      await navigator.clipboard.writeText(window.location.href);
      setSnackbar({
        open: true,
        message: 'Link copied to clipboard!',
        severity: 'success'
      });
    } catch (clipError) {
      try {
        const tempInput = document.createElement('input');
        tempInput.value = window.location.href;
        tempInput.style.position = 'fixed';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.select();
        tempInput.setSelectionRange(0, 99999);
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        
        setSnackbar({
          open: true,
          message: 'Link copied to clipboard!',
          severity: 'success'
        });
      } catch (finalError) {
        setSnackbar({
          open: true,
          message: 'Could not copy. Link: ' + window.location.href.substring(0, 30) + '...',
          severity: 'info'
        });
      }
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#1a7f3a' }} />
      </Box>
    );
  }

  if (error || !deal) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Deal not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ color: '#1a7f3a' }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const darkChipColor = getDarkCategoryChipColor(deal.category);

  // Use the image URL from the deal
  const rawImageUrl = deal.deal_url || deal.image_url || deal.img_url;
  const finalImageUrl = rawImageUrl 
    ? getImageUrl(rawImageUrl) 
    : 'https://via.placeholder.com/800x600?text=No+Image';

  return (
    <Box sx={{ bgcolor: '#F7F7F7', minHeight: '100vh', py: isMobile ? 2 : 4 }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
        <Box sx={{ mb: 2, px: isMobile ? 2 : 0 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              color: '#1a7f3a',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Back to deals
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr',
            gap: isMobile ? 0 : 3,
            mb: 4,
          }}
        >
          {/* Left Column - Main Image Banner */}
          <Card sx={{ borderRadius: isMobile ? 0 : 2, boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                height={isMobile ? "300" : "400"}
                image={finalImageUrl}
                alt={deal.deal_name}
                sx={{
                  objectFit: 'cover',
                  display: 'block',
                  width: '100%',
                }}
              />

              {/* Gradient overlay for readability if we put text on top */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  p: 3,
                  pt: 6,
                }}
              >
                <Chip
                  label={deal.category}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.95)',
                    color: darkChipColor,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    mb: 1,
                    width: 'max-content',
                  }}
                />
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    lineHeight: 1.3,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                  }}
                >
                  {deal.deal_name}
                </Typography>
              </Box>
              
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: '#FF4444',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  boxShadow: '0 4px 12px rgba(255,68,68,0.5)',
                  zIndex: 3,
                }}
              >
                {deal.percentage_discount}% OFF
              </Box>

              {deal.distance && (
                <Chip
                  icon={<Navigation />}
                  label={`${deal.distance} km`}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    fontWeight: 700,
                    bgcolor: '#1a7f3a',
                    color: 'white',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    height: isMobile ? 28 : 32,
                    backdropFilter: 'blur(10px)',
                    zIndex: 3,
                    '& .MuiChip-icon': {
                      color: 'white',
                    },
                  }}
                />
              )}
            </Box>

            <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={isFavorite ? <Favorite /> : <FavoriteBorder />}
                onClick={handleToggleFavorite}
                sx={{
                  borderColor: '#D5D9D9',
                  color: isFavorite ? '#CC0C39' : '#0F1111',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: '#1a7f3a',
                    bgcolor: 'rgba(26, 127, 58, 0.04)',
                  },
                }}
              >
                {isFavorite ? 'Saved' : 'Save'}
              </Button>
              <IconButton
                onClick={handleShare}
                sx={{
                  border: '1px solid #D5D9D9',
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: '#1a7f3a',
                    bgcolor: 'rgba(26, 127, 58, 0.04)',
                  },
                }}
              >
                <Share />
              </IconButton>
            </Box>
          </Card>

          {/* Right Column - Details */}
          <Card sx={{ p: isMobile ? 2 : 3, borderRadius: isMobile ? 0 : 2, boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#007185',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 1,
                display: 'block',
              }}
            >
              {deal.category}
            </Typography>

            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{
                color: '#0F1111',
                fontWeight: 500,
                mb: 2,
                lineHeight: 1.3,
              }}
            >
              {deal.deal_name}
            </Typography>

            {/* ✅ Navigate to vendor details when clicking store name */}
            <Stack 
              direction="row" 
              spacing={1} 
              alignItems="center" 
              sx={{ 
                mb: 2, 
                cursor: 'pointer', 
                display: 'inline-flex',
                '&:hover .vendor-text': { textDecoration: 'underline' } 
              }}
              onClick={() => navigate(`/vendor/${deal.vendor_id}`)}
            >
              <Store sx={{ fontSize: 20, color: '#565959' }} />
              <Typography
                className="vendor-text"
                variant="body1"
                sx={{
                  color: '#007185',
                  fontWeight: 500,
                }}
              >
                {deal.vendor_name}
              </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }}>
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  sx={{
                    color: '#B12704',
                    fontWeight: 500,
                  }}
                >
                  ₹{deal.discounted_price}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    textDecoration: 'line-through',
                    color: '#565959',
                  }}
                >
                  ₹{deal.original_price}
                </Typography>
              </Stack>
              <Typography
                variant="body1"
                sx={{
                  color: '#1a7f3a',
                  fontWeight: 600,
                }}
              >
                Save ₹{deal.discount} ({deal.percentage_discount}% off)
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#0F1111', fontWeight: 600, mb: 1 }}>
                About this deal
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#565959',
                  lineHeight: 1.6,
                }}
              >
                {deal.description}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="h6" sx={{ color: '#0F1111', fontWeight: 600, mb: 2 }}>
                Deal Information
              </Typography>
              
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <LocationOn sx={{ fontSize: 20, color: '#1a7f3a', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#0F1111', fontWeight: 600 }}>
                      Location
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#565959' }}>
                      {deal.address}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#565959', display: 'block', mb: 1 }}>
                      {deal.city}
                    </Typography>

                    {/* ✅ GOOGLE MAP BUTTON */}
                    {deal.location?.coordinates && (
                      <Button
                        variant="outlined"
                        startIcon={<Navigation />}
                        onClick={openInGoogleMaps}
                        size="small"
                        sx={{
                          borderColor: '#1a7f3a',
                          color: '#1a7f3a',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            bgcolor: '#E8F5E9',
                            borderColor: '#1a7f3a',
                          },
                        }}
                      >
                        Open in Google Maps
                      </Button>
                    )}
                  </Box>
                </Stack>

                {/* ✅ Safe Check: Only render validity if it's a valid parseable date */}
                {isValidDate(deal.validity) && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime sx={{ fontSize: 20, color: '#1a7f3a' }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: '#0F1111', fontWeight: 600 }}>
                        Valid Until
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#565959' }}>
                        {new Date(deal.validity).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Box>

          </Card>
        </Box>

        {/* Similar Deals - WITH DISTANCE */}
        {similarDeals.length > 0 && (
          <Box sx={{ 
            bgcolor: 'white', 
            p: isMobile ? 0 : 3, 
            borderRadius: isMobile ? 0 : 2, 
            boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
            mx: isMobile ? 0 : 2,
          }}>
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="center"
              sx={{ 
                mb: isMobile ? 1.5 : 3,
                px: isMobile ? 2 : 0,
                pt: isMobile ? 2 : 0,
              }}
            >
              <Typography 
                variant={isMobile ? 'h6' : 'h5'} 
                sx={{ color: '#0F1111', fontWeight: 600 }}
              >
                Similar Deals Near You
              </Typography>
              <Button
                variant="text"
                onClick={() => navigate('/deals')}
                sx={{
                  color: '#1a7f3a',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                View All
              </Button>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: isMobile ? 0 : 2,
              }}
            >
              {similarDeals.map((similarDeal, index) => {
                const dealId = similarDeal._id;
                const isDealFavorite = favorites.includes(dealId);
                const dealDarkChipColor = getDarkCategoryChipColor(similarDeal.category);

                const simRawUrl = similarDeal.deal_url || similarDeal.image_url || similarDeal.img_url;
                const simFinalUrl = simRawUrl 
                  ? getImageUrl(simRawUrl) 
                  : 'https://via.placeholder.com/400x300?text=No+Image';

                return (
                  <Card
                    key={dealId}
                    onClick={() => navigate(`/deal/${dealId}`)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.08)',
                      borderRadius: isMobile ? 0 : 1,
                      border: isMobile ? '2px solid #E5E7EB' : 'none',
                      borderRight: isMobile ? '2px solid #E5E7EB' : 'none',
                      borderBottom: isMobile ? '2px solid #E5E7EB' : 'none',
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: isMobile ? 'none' : '0 8px 25px rgba(0,0,0,0.15)',
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        height: isMobile ? 140 : 180,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <CardMedia
                        component="img"
                        height={isMobile ? "140" : "180"}
                        image={simFinalUrl}
                        alt={similarDeal.deal_name}
                        sx={{
                          objectFit: 'cover',
                          userSelect: 'none',
                          pointerEvents: 'none',
                          width: '100%',
                        }}
                      />

                      {/* Distance Chip - Top Left */}
                      {similarDeal.distance !== undefined && similarDeal.distance !== null && (
                        <Chip
                          icon={<Navigation />}
                          label={`${similarDeal.distance} km`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: isMobile ? 8 : 12,
                            left: isMobile ? 8 : 12,
                            fontWeight: 700,
                            bgcolor: '#1a7f3a',
                            color: 'white',
                            fontSize: isMobile ? '0.65rem' : '0.7rem',
                            height: isMobile ? 20 : 24,
                            backdropFilter: 'blur(10px)',
                            zIndex: 3,
                            '& .MuiChip-icon': {
                              color: 'white',
                            },
                          }}
                        />
                      )}

                      {/* Discount Chip - Top Right */}
                      <Chip
                        label={`${similarDeal.percentage_discount}% OFF`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: isMobile ? 8 : 12,
                          right: isMobile ? 8 : 12,
                          fontWeight: 700,
                          bgcolor: '#FF4444',
                          color: 'white',
                          fontSize: isMobile ? '0.65rem' : '0.75rem',
                          height: isMobile ? 20 : 24,
                          boxShadow: '0 4px 12px rgba(255,68,68,0.5)',
                          zIndex: 3,
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: isMobile ? 1 : 2, pt: isMobile ? 1 : 2 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: isMobile ? 0.5 : 1 }}>
                        <Chip
                          label={similarDeal.category}
                          size="small"
                          sx={{
                            bgcolor: dealDarkChipColor,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            height: isMobile ? 18 : 24,
                            width: '100%',
                            justifyContent: 'center',
                          }}
                        />
                      </Stack>

                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#212121',
                          fontSize: isMobile ? '0.8rem' : '0.95rem',
                        }}
                      >
                        {similarDeal.deal_name}
                      </Typography>

                      {/* ✅ Navigate to vendor details from Similar Deals card as well */}
                      <Stack 
                        direction="row" 
                        spacing={0.5} 
                        alignItems="center" 
                        sx={{ 
                          mb: isMobile ? 0.5 : 1,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          '&:hover .vendor-text': { color: '#1a7f3a', textDecoration: 'underline' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/vendor/${similarDeal.vendor_id}`);
                        }}
                      >
                        <Store sx={{ fontSize: isMobile ? 12 : 16, color: '#1a7f3a' }} />
                        <Typography
                          className="vendor-text"
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.65rem' : '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            transition: 'color 0.2s',
                          }}
                        >
                          {similarDeal.vendor_name}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: isMobile ? 0.5 : 1.5 }}>
                        <Typography
                          variant={isMobile ? "subtitle1" : "h6"}
                          fontWeight={700}
                          sx={{ 
                            color: '#1a7f3a',
                            fontSize: isMobile ? '0.95rem' : '1.25rem',
                          }}
                        >
                          ₹{similarDeal.discounted_price}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: 'line-through',
                            color: '#878787',
                            fontSize: isMobile ? '0.65rem' : '0.8rem',
                          }}
                        >
                          ₹{similarDeal.original_price}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: isMobile ? 0.8 : 2 }}>
                        <LocationOn sx={{ fontSize: isMobile ? 14 : 16, color: '#1a7f3a' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {similarDeal.address}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack
                          direction="row"
                          spacing={0.3}
                          alignItems="center"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavoriteForSimilar(dealId);
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.3s ease', 
                            minWidth: isMobile ? '45px' : '60px',
                          }}
                        >
                          {isDealFavorite ? (
                            <Favorite sx={{ color: '#FF6B6B', fontSize: isMobile ? 14 : 20 }} />
                          ) : (
                            <FavoriteBorder sx={{ color: 'text.secondary', fontSize: isMobile ? 14 : 20 }} />
                          )}
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: isMobile ? '0.55rem' : '0.7rem' }}
                          >
                            {isDealFavorite ? 'Saved' : 'Save'}
                          </Typography>
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/deal/${dealId}`);
                          }}
                          sx={{
                            borderColor: '#1a7f3a',
                            color: '#1a7f3a',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                            py: isMobile ? 0.3 : 0.5,
                            px: isMobile ? 1 : 2,
                            borderRadius: isMobile ? 12 : 20,
                            minHeight: isMobile ? 24 : 32,
                            '&:hover': {
                              borderColor: '#1a7f3a',
                              bgcolor: 'rgba(255,255,255,0.1)',
                            },
                          }}
                        >
                          View
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {hasMore && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  minHeight: '100px',
                }}
              >
                {loadingMore && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} sx={{ color: '#1a7f3a' }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more deals...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {!hasMore && similarDeals.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 3, px: isMobile ? 2 : 0 }}>
                <Typography variant="body2" color="text.secondary">
                  🎉 You've seen all similar deals
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DealDetails;